import type { ResolvedTheme } from '@/types/client-config'

const REQUIRED_COLOR_KEYS = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
] as const

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function toStringRecord(obj: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = value
    }
  }
  return result
}

function resolveTheme(raw: unknown): ResolvedTheme {
  if (!isObject(raw)) {
    throw new Error('Theme must be a JSON object')
  }

  let shared: Record<string, unknown> = {}
  let light: Record<string, unknown>
  let dark: Record<string, unknown>

  if (isObject(raw.cssVars)) {
    const cssVars = raw.cssVars as Record<string, unknown>
    if (isObject(cssVars.theme)) {
      shared = cssVars.theme as Record<string, unknown>
    }
    if (!isObject(cssVars.light)) {
      throw new Error('Missing required field: cssVars.light (must be an object)')
    }
    if (!isObject(cssVars.dark)) {
      throw new Error('Missing required field: cssVars.dark (must be an object)')
    }
    light = cssVars.light as Record<string, unknown>
    dark = cssVars.dark as Record<string, unknown>
  } else if (isObject(raw.light) && isObject(raw.dark)) {
    light = raw.light as Record<string, unknown>
    dark = raw.dark as Record<string, unknown>
  } else {
    throw new Error(
      'Invalid theme format: expected either tweakcn format (with cssVars) or bare format (with light/dark)',
    )
  }

  for (const mode of ['light', 'dark'] as const) {
    const vars = mode === 'light' ? light : dark
    for (const key of REQUIRED_COLOR_KEYS) {
      if (typeof vars[key] !== 'string' || (vars[key] as string).length === 0) {
        throw new Error(`Missing required theme variable: ${mode}.${key}`)
      }
    }
  }

  return {
    shared: toStringRecord(shared),
    light: toStringRecord(light),
    dark: toStringRecord(dark),
  }
}

export async function loadTheme(): Promise<ResolvedTheme> {
  let response: Response
  try {
    response = await fetch('/config/theme.json')
  } catch (err) {
    throw new Error(
      `Failed to fetch /config/theme.json: ${err instanceof Error ? err.message : err}`,
    )
  }

  if (!response.ok) {
    throw new Error(`Failed to load /config/theme.json: HTTP ${response.status}`)
  }

  const raw: unknown = await response.json()
  return resolveTheme(raw)
}
